import * as RG from '@radix-ui/react-radio-group';
import styled from 'styled-components';
import {
  body2Medium,
  borderRadius,
  caption,
  colors,
  duration,
  easing,
  spacing,
  subtleShadow,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { Option } from './types';

export const RGRoot = styled(RG.Root)`
  display: grid;
  grid-auto-flow: ${({ orientation }) => (orientation === 'horizontal' ? 'column' : 'row')};
  gap: ${spacing.half};
`;
export const RGItem = styled(RG.Item)<{ $isBordered: boolean }>`
  all: unset;
  background-color: ${colors.backgroundPrimary};
  width: ${spacing.plusQuarter};
  height: ${spacing.plusQuarter};
  min-width: ${spacing.plusQuarter};
  border-radius: 100%;
  transition: all ${duration.short} ${easing.entrance};
  ${subtleShadow};
  border: 1px solid ${colors.borderStrong};
  &[data-state='checked'] {
    background-color: ${colors.backgroundSuccess};
    border: 1px solid ${colors.borderSuccess};
    &[data-disabled] {
      background-color: ${colors.backgroundDisabledStrong};
      border: 1px solid ${colors.backgroundDisabledStrong};
    }
  }
  ${({ $isBordered }) =>
    !$isBordered &&
    `&:hover:not([data-state='checked']):not([data-disabled]) {
    background-color: ${colors.backgroundPrimary};
    border: 1px solid ${colors.borderEmphasis};
  }`}
  &[data-disabled] {
    cursor: not-allowed;
    background-color: ${colors.backgroundDisabled};
  }
  &:focus-visible:not([data-state='checked'], [data-disabled]) {
    outline: solid 1px ${colors.borderOnColor};
    box-shadow: 0 0 0 ${pxToRem(2.5)} ${colors.borderStrong};
  }
  &:focus-visible[data-state='checked']:not([data-disabled]) {
    outline: solid 1px white;
    box-shadow: 0 0 0 ${pxToRem(2.5)} ${colors.borderSuccess};
  }
`;
export const RGIndicator = styled(RG.Indicator)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
  &::after {
    content: '';
    display: block;
    width: ${spacing.threeEights};
    height: ${spacing.threeEights};
    border-radius: ${borderRadius.full};
    background-color: ${colors.backgroundPrimary};
  }
`;

export const Label = styled.label<{ disabled: Required<Option['isDisabled']>; overlap: boolean }>`
  ${body2Medium};
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.textColors.textDisabled : theme.colors.textColors.textPrimary};
  user-select: none;
  ${({ overlap }) =>
    overlap &&
    `display: flex;
     flex-direction: column;
     align-items: center;
     position: relative;
     left: 20px;
    `}
`;

export const GroupLabel = styled.span`
  display: block;
`;

export const Description = styled.div<{
  disabled: Required<Option['isDisabled']>;
  centered: boolean;
}>`
  ${caption};
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.textColors.textDisabled : theme.colors.textColors.textSecondary};
  ${({ centered }) =>
    centered &&
    `text-align: center;
     margin-top: ${spacing.quarter};
    `}
`;

export const Container = styled.div<{
  $isBordered: boolean;
  $isDisabled: Required<Option['isDisabled']>;
  $isSelected: boolean;
  $alignItems?: 'center' | 'flex-start';
  $alignRight?: boolean;
}>`
  display: flex;
  gap: ${spacing.threeQuarter};
  align-items: ${({ $alignItems }) => $alignItems ?? 'center'};
  transition: border ${duration.short} ${easing.entrance};

  ${({ $alignRight }) =>
    $alignRight &&
    `flex-direction: row-reverse;
     align-items: flex-start;
     justify-content: center;
    `}

  ${({ $isBordered }) =>
    $isBordered &&
    `padding: ${spacing.base} ${spacing.threeQuarter} ${spacing.base} ${spacing.threeQuarter};
     border: 1px solid ${colors.borderPrimary};
     box-shadow: ${subtleShadow.boxShadow};
     border-radius: ${borderRadius.lg};
     &:focus {
        outline: solid 1px white;
        box-shadow: 0 0 0 ${pxToRem(2.5)} ${colors.borderEmphasis};
     }
    `}

  ${({ $isBordered, $isDisabled, $isSelected }) => {
    if (!$isDisabled && $isBordered) {
      if ($isSelected) {
        return `
        outline: 1px solid ${colors.borderSuccess};
        border: 1px solid ${colors.borderSuccess};
        `;
      }
      return `
        &:hover {
        border: 1px solid ${colors.borderEmphasis};
      }`;
    }
    if ($isDisabled && $isBordered) {
      if ($isSelected) {
        return `
          outline: 1px solid ${colors.borderEmphasis};
          border: 1px solid ${colors.borderEmphasis};
          cursor: not-allowed;
          background-color: ${colors.backgroundDisabled};      
      `;
      }
      return `
        cursor: not-allowed;
        background-color: ${colors.backgroundDisabled};       
      `;
    }
    return '';
  }}
`;
